import {
    useCallback,
    useLayoutEffect,
    useRef,
    useState,
    type CompositionEvent,
    type CSSProperties,
    type ReactNode,
    type UIEvent,
} from "react";
import { adTokenSegments, type AdTokenKind, type AdTokenRange } from "./adTokenScan";
import "./adTokenHighlight.css";

/**
 * Ad tokens colored in place, behind an ordinary text field (MPN-1930).
 *
 * A div behind the field, laid out exactly like it, holds the same text with the tokens
 * colored. The field's own text goes transparent and its caret does not, so what the writer
 * sees is the backdrop's text through a field that still edits, selects, undoes and pastes
 * like any other -- not contenteditable, and nothing about editing changes.
 *
 * Works for a wrapping `<textarea>` (ad copy) and a one-line `<input>` (Create Studio's text
 * layers, MPN-1933). The field is the caller's: this renders it through `children`, handing
 * over the props it needs -- a ref, the style, and three handlers to compose with the
 * caller's own.
 *
 * Only on while there is something to color. With no tokens in the text, or none to check
 * against, the field renders exactly as it would without this.
 */

type Field = HTMLTextAreaElement | HTMLInputElement;

export type AdTokenHighlightFieldProps = {
    ref: (node: Field | null) => void;
    className?: string;
    style?: CSSProperties;
    onScroll: (e: UIEvent<Field>) => void;
    onCompositionStart: (e: CompositionEvent<Field>) => void;
    onCompositionEnd: (e: CompositionEvent<Field>) => void;
};

/**
 * How each kind looks behind the text. Background, color and decoration only: anything that
 * takes room -- padding, a border, a bolder weight -- would move the backdrop's text off the
 * field's. The instruction pill's padding is a box-shadow for the same reason.
 */
const HIGHLIGHT: Record<AdTokenKind, CSSProperties> = {
    known: { background: "#e3edfc", borderRadius: 3 },
    alias: {
        background: "#fdecc8", borderRadius: 3,
        textDecoration: "underline dotted #b45309", textUnderlineOffset: 2,
    },
    instruction: {
        background: "#fdf3e3", color: "#8f5405", borderRadius: 4,
        boxShadow: "0 0 0 2px #fdf3e3",
    },
    unknown: {
        background: "#fee2e2", borderRadius: 3,
        textDecoration: "underline wavy #dc2626", textDecorationSkipInk: "none",
    },
};

/**
 * Every computed property that decides where a character lands. Width and height go across
 * as computed values rather than offsetWidth, which rounds: a box half a pixel narrower
 * wraps a long line a word early, and every highlight after that sits on the wrong text.
 */
const LAYOUT_PROPERTIES = [
    "boxSizing", "width", "height",
    "paddingTop", "paddingRight", "paddingBottom", "paddingLeft",
    "borderTopWidth", "borderRightWidth", "borderBottomWidth", "borderLeftWidth",
    "fontFamily", "fontSize", "fontWeight", "fontStyle", "fontVariant", "fontStretch",
    "fontKerning", "fontFeatureSettings", "fontVariationSettings",
    "lineHeight", "letterSpacing", "wordSpacing", "textTransform", "textIndent", "textAlign",
    "tabSize", "direction",
] as const;

const px = (v: string) => parseFloat(v) || 0;

/**
 * Lay the backdrop out exactly like the field. The border is copied as width only, and
 * transparent, so the text starts at the same inset; the field's own border is the one that
 * shows. A scrollbar in the field takes width from its text, so the backdrop gives up the
 * same width as padding.
 *
 * A one-line input centers its line in its box and a div does not, so for an input the line
 * is made as tall as the box's content -- the same centering, done by line-height.
 */
export function layOutLike(field: Field, backdrop: HTMLDivElement, multiline: boolean): void {
    const computed = window.getComputedStyle(field);
    for (const property of LAYOUT_PROPERTIES) backdrop.style[property] = computed[property];
    backdrop.style.borderStyle = "solid";
    backdrop.style.borderColor = "transparent";
    const scrollbar = field.offsetWidth - field.clientWidth
        - px(computed.borderLeftWidth) - px(computed.borderRightWidth);
    if (scrollbar > 0) backdrop.style.paddingRight = `${px(computed.paddingRight) + scrollbar}px`;
    if (!multiline) {
        const content = field.clientHeight - px(computed.paddingTop) - px(computed.paddingBottom);
        if (content > 0) backdrop.style.lineHeight = `${content}px`;
    }
    backdrop.scrollTop = field.scrollTop;
    backdrop.scrollLeft = field.scrollLeft;
}

/**
 * What can move an input's text sideways. An input scrolls to keep its caret in view and
 * says nothing about it -- no scroll event on every engine -- so the backdrop follows on
 * anything that can move the caret.
 */
const CARET_EVENTS = ["input", "keydown", "keyup", "select", "mousedown", "mouseup", "mousemove", "focus"] as const;

export default function AdTokenHighlight({ value, ranges, multiline, className, style, children }: {
    value: string;
    /** The tokens to color, from scanAdTokens or useAdTokenCheck -- for `value` as it is now. */
    ranges: AdTokenRange[];
    /** A wrapping textarea, or a one-line input that scrolls sideways. */
    multiline: boolean;
    /** The field's own class and style, which this adjusts while it is highlighting. */
    className?: string;
    style?: CSSProperties;
    children: (field: AdTokenHighlightFieldProps) => ReactNode;
}) {
    const fieldRef = useRef<Field | null>(null);
    const backdropRef = useRef<HTMLDivElement>(null);
    /**
     * An IME is mid-word. The text it is still composing is drawn by the field itself,
     * underline and all, so for the length of a composition the field shows its own text and
     * the highlights step aside. They come back on compositionend.
     */
    const [composing, setComposing] = useState(false);
    const highlighting = ranges.length > 0 && !composing;
    const textColor = String(style?.color ?? "inherit");

    /*
        The caret's own colour, measured off the backdrop.

        The field's text is set transparent so the backdrop's coloured copy shows through, and
        the caret has to be exempted from that. `caret-color: inherit` is not an exemption:
        it inherits the wrapper's `auto`, and `auto` resolves to the ELEMENT's colour, which
        is the transparent one. A caller that styles its field by class rather than with an
        inline `color` therefore got an invisible cursor -- the Email & SMS copy editor did,
        and the only way to see where you were typing was to leave highlighting via Advanced.

        The backdrop carries the real colour, inherited exactly as the field's would have
        been, so reading it back is the one answer that is right for every caller.
    */
    const [measured, setMeasured] = useState<string | null>(null);
    useLayoutEffect(() => {
        const backdrop = backdropRef.current;
        if (!highlighting || !backdrop) return;
        const resolved = window.getComputedStyle(backdrop).color;
        setMeasured((prev) => (resolved && resolved !== prev ? resolved : prev));
    }, [highlighting, style?.color]);

    const caretColor = style?.color !== undefined ? textColor : (measured ?? "inherit");

    const syncScroll = useCallback(() => {
        const field = fieldRef.current;
        const backdrop = backdropRef.current;
        if (!field || !backdrop) return;
        backdrop.scrollTop = field.scrollTop;
        backdrop.scrollLeft = field.scrollLeft;
    }, []);

    // After every change of text, and whenever the box changes size: an autogrow, a narrower
    // panel, or a web font arriving and reflowing the text.
    useLayoutEffect(() => {
        const field = fieldRef.current;
        const backdrop = backdropRef.current;
        if (!highlighting || !field || !backdrop) return;
        const sync = () => layOutLike(field, backdrop, multiline);
        sync();
        const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(sync);
        observer?.observe(field);
        const fonts = typeof document !== "undefined" ? document.fonts : undefined;
        fonts?.addEventListener?.("loadingdone", sync);
        // The caret moves before keyup on a held key, so a frame later catches it too.
        const follow = () => {
            syncScroll();
            requestAnimationFrame(syncScroll);
        };
        if (!multiline) for (const type of CARET_EVENTS) field.addEventListener(type, follow);
        return () => {
            observer?.disconnect();
            fonts?.removeEventListener?.("loadingdone", sync);
            if (!multiline) for (const type of CARET_EVENTS) field.removeEventListener(type, follow);
        };
    }, [highlighting, multiline, value, syncScroll]);

    const field: AdTokenHighlightFieldProps = {
        ref: useCallback((node: Field | null) => { fieldRef.current = node; }, []),
        className: [className, highlighting && "ad-token-field"].filter(Boolean).join(" ") || undefined,
        style: highlighting ? {
            ...style,
            position: "relative",
            background: "transparent",
            color: "transparent",
            caretColor,
            // The selection is drawn by the stylesheet's ::selection, as a tint over the
            // backdrop's text.
        } : style,
        onScroll: syncScroll,
        onCompositionStart: () => setComposing(true),
        onCompositionEnd: () => setComposing(false),
    };

    return (
        <div style={{ position: "relative" }}>
            {highlighting && (
                <div
                    ref={backdropRef}
                    aria-hidden="true"
                    data-testid="ad-token-backdrop"
                    style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        margin: 0,
                        overflow: "hidden",
                        pointerEvents: "none",
                        whiteSpace: multiline ? "pre-wrap" : "pre",
                        overflowWrap: multiline ? "break-word" : "normal",
                        wordBreak: "normal",
                        color: textColor,
                        background: style?.background ?? style?.backgroundColor ?? "#fff",
                        borderRadius: style?.borderRadius,
                    }}
                >
                    {adTokenSegments(value, ranges).map((segment, i) =>
                        segment.kind ? (
                            <span
                                key={i}
                                data-kind={segment.kind}
                                style={{ ...HIGHLIGHT[segment.kind], boxDecorationBreak: "clone", WebkitBoxDecorationBreak: "clone" }}
                            >
                                {segment.text}
                            </span>
                        ) : (
                            segment.text
                        ),
                    )}
                    {/* A trailing line break only makes a line in a div if something follows it;
                        a textarea makes one regardless. */}
                    {multiline && value.endsWith("\n") && "​"}
                </div>
            )}
            {children(field)}
        </div>
    );
}
